// src/pages/MonthlyCalendarPage.js

import React, { useEffect, useMemo, useState, useRef } from "react";
import { CaretLeft, CaretRight, Star } from "phosphor-react";
import BackButton from "../components/BackButton";
import ActivityLightbox from "../components/ActivityLightbox";
import { api } from "../api";
import useStreakDays from "../hooks/useStreakDays";

const pad2 = (n) => String(n).padStart(2, "0");

const dateKey = (val) => {
  if (!val) return null;
  if (typeof val === "string") {
    const m = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  }
  const dt = new Date(val);
  if (Number.isNaN(dt)) return null;
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
};

const groupByDay = (list = []) => {
  const map = {};
  list.forEach((a) => {
    const key = dateKey(a.timestamp ?? a.created_at);
    if (!key) return;
    (map[key] ||= []).push(a);
  });
  Object.values(map).forEach((arr) =>
    arr.sort(
      (a, b) =>
        new Date(a.timestamp ?? a.created_at) -
        new Date(b.timestamp ?? b.created_at)
    )
  );
  return map;
};

async function getToken() {
  try {
    const { Preferences } = await import("@capacitor/preferences");
    const pref = await Preferences.get({ key: "auth_token" });
    if (pref?.value) return pref.value;
  } catch {}

  return (
    localStorage.getItem("token") ||
    sessionStorage.getItem("token") ||
    null
  );
}

async function getAuthHeaders() {
  const token = await getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const withUserFallback = (a, me) => {
  if (!a) return a;
  const hasUser = a.user && (a.user.id || a.user.name);
  if (hasUser || !me) return a;

  return {
    ...a,
    user: {
      id: me.id,
      name:
        me.name ||
        me.display_name ||
        [me.first_name, me.last_name].filter(Boolean).join(" ") ||
        me.username ||
        `User #${me.id}`,
      profile_pic_url: me.profile_pic_url || me.avatar_url || null,
    },
  };
};

export default function MonthlyCalendarPage() {
  const [me, setMe] = useState(null);
  const [activitiesByDay, setActivitiesByDay] = useState({});
  const [lb, setLb] = useState({ open: false, activity: null, id: null });
  const [isMobile, setIsMobile] = useState(false);

  const headerRef = useRef(null);
  const weekdayRef = useRef(null);

  useEffect(() => {
    const check = () =>
      setIsMobile(window.matchMedia("(max-width: 767px)").matches);

    check();
    window.addEventListener("resize", check);

    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const meRes = await api.get("/users/me", { headers });
        const meObj = meRes?.data || null;
        setMe(meObj);

        if (!meObj?.id) {
          setActivitiesByDay({});
          return;
        }

        const actsRes = await api.get(`/users/${meObj.id}/activities`, {
          headers,
        });

        const raw = Array.isArray(actsRes?.data) ? actsRes.data : [];
        const hydrated = raw.map((a) => withUserFallback(a, meObj));
        setActivitiesByDay(groupByDay(hydrated));
      } catch (e) {
        console.error("MonthlyCalendarPage load failed:", e);
        setActivitiesByDay({});
      }
    })();
  }, []);

  const computedStreak = useStreakDays({ activitiesByDay });

  const [viewDate, setViewDate] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const viewYear = viewDate.getFullYear();
  const viewMonth = viewDate.getMonth();

  const monthShort = viewDate
    .toLocaleString("en-US", { month: "short" })
    .toUpperCase();

  const addMonths = (date, delta) => {
    const d = new Date(date);
    d.setMonth(d.getMonth() + delta);
    return new Date(d.getFullYear(), d.getMonth(), 1);
  };

  const toYMD = (y, m, d) => `${y}-${pad2(m + 1)}-${pad2(d)}`;

  const first = new Date(viewYear, viewMonth, 1);
  const startDow = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

  const cells = useMemo(() => {
    const arr = [];
    const total = Math.ceil((startDow + daysInMonth) / 7) * 7;

    for (let i = 0; i < total; i += 1) {
      const dayNum = i - startDow + 1;

      if (dayNum < 1 || dayNum > daysInMonth) {
        arr.push({ label: "", ymd: null, stars: [] });
        continue;
      }

      const ymdKey = toYMD(viewYear, viewMonth, dayNum);

      const stars = (activitiesByDay[ymdKey] || [])
        .slice()
        .sort(
          (a, b) =>
            new Date(a.timestamp || a.created_at) -
            new Date(b.timestamp || b.created_at)
        );

      arr.push({ label: String(dayNum), ymd: ymdKey, stars });
    }

    return arr;
  }, [activitiesByDay, daysInMonth, startDow, viewMonth, viewYear]);

  return (
    <div className="min-h-[100svh] bg-blue-50">
      <header
        ref={headerRef}
        className="sticky top-0 left-0 right-0 z-30 bg-blue-50"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="mx-auto mt-1 flex max-w-5xl items-center px-3 sm:mt-2 sm:px-4">
          <BackButton className="text-blue-600" ariaLabel="Back" />

          <div className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center">
            <button
              type="button"
              onClick={() => setViewDate((d) => addMonths(d, -1))}
              className="grid h-8 w-8 place-items-center rounded-full text-blue-400 transition hover:bg-white"
              aria-label="Previous month"
              title="Previous month"
            >
              <CaretLeft size={23} weight="fill" className="text-blue-400" />
            </button>

            <h1 className="text-[25px] text-blue-800 dm-font font-black tracking-[-0.06em] leading-[0.9] select-none">
              {monthShort}
            </h1>

            <button
              type="button"
              onClick={() => setViewDate((d) => addMonths(d, 1))}
              className="grid h-9 w-9 place-items-center rounded-full text-blue-400 transition hover:bg-white"
              aria-label="Next month"
              title="Next month"
            >
              <CaretRight size={23} weight="fill" className="text-blue-400" />
            </button>
          </div>
        </div>

        <div
          ref={weekdayRef}
          className="mx-auto mb-1 mt-0.5 grid grid-cols-7 gap-0 px-2 font-semibold text-blue-800 sm:mt-1"
        >
          {["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"].map((d, i) => (
            <div
              key={d}
              className={`${i === 0 ? "pl-2" : "pl-1"} text-left text-sm`}
            >
              {d}
            </div>
          ))}
        </div>
      </header>

      <main className="mx-auto px-2 pb-2">
        <div className="grid grid-cols-7 gap-0 overflow-hidden rounded-2xl border border-blue-200/60">
          {cells.map((c, idx) => {
            const maxStars = isMobile ? 6 : 8;
            const extra = Math.max(0, (c.stars?.length || 0) - maxStars);
            const starsToShow = (c.stars || []).slice(0, maxStars);
            const dense = (c.stars?.length || 0) > (isMobile ? 3 : 4);
            const starSize = dense ? (isMobile ? 10 : 13) : 14;

            return (
              <div
                key={idx}
                className="bg-white/80 relative border-r border-b border-blue-200/60 p-1.5 md:p-2 min-h-[92px] md:min-h-[130px] flex flex-col overflow-hidden"
              >
                {c.label && (
                  <div className="text-blue-700 text-sm font-semibold">
                    {c.label}
                  </div>
                )}

                {(starsToShow.length > 0 || extra > 0) && (
                  <div className="-mt-2 grid grid-cols-2 auto-rows-max gap-x-0.5 gap-y-0.5">
                    {starsToShow.map((a) => (
                      <button
                        key={a.id}
                        title="View activity"
                        onClick={() =>
                          setLb({
                            open: true,
                            activity: null,
                            id: a.id,
                          })
                        }
                        className="p-0.5 rounded active:scale-95 transition"
                      >
                        <Star
                          size={starSize}
                          weight="fill"
                          className="text-blue-400 hover:text-blue-800 transition-colors cursor-pointer"
                        />
                      </button>
                    ))}

                    {extra > 0 && (
                      <span className="text-[10px] leading-none px-1 py-0.5 rounded bg-blue-100 text-blue-700 self-center justify-self-start">
                        +{extra}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {computedStreak >= 1 && (
          <div className="mt-3 flex items-center justify-center text-blue-800">
            <div className="rounded-full border border-blue-100 bg-blue-200 px-3 !py-0.5 !min-h-0 text-blue-700 shadow-[0_2px_0px_rgba(37,99,235,0.96)]">
              <span className="font-medium text-sm">
                {computedStreak}-day streak, keep going!
              </span>
            </div>
          </div>
        )}
      </main>

      <ActivityLightbox
        open={lb.open}
        onClose={() => setLb({ open: false, activity: null, id: null })}
        activity={null}
        activityId={lb.id}
        currentUser={me || undefined}
        className="p-4"
      />
    </div>
  );
}